/**
 * CheckersMultiplayerGame — live 2-player Checkers renderer.
 *
 * Mirrors the shape of FourInARowGame: connect to /ws/game/{id}, apply
 * `checkers_state` broadcasts to local state, dispatch `checkers_move` /
 * `checkers_huff` back over the same channel. Side panels show entry /
 * prize (backend rake_1v1) / whose turn / turn-timer countdown.
 *
 * Server-authoritative — this component never mutates the board itself;
 * it just relays clicks to the server and re-renders on the reply.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckersBoard } from '../checkers_ui';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString();

const CheckersMultiplayerGame = ({ user }) => {
  const { gameId } = useParams();
  const navigate = useNavigate();

  const [game, setGame] = useState(null);
  const [state, setState] = useState(null);          // checkers engine state
  const [colorMap, setColorMap] = useState({});
  const [economyRates, setEconomyRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Turn timer countdown (seconds).
  const [secondsLeft, setSecondsLeft] = useState(null);
  const wsRef = useRef(null);

  // ---- Initial fetch of the game doc + economy rates -------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [g, r] = await Promise.all([
          axios.get(`${API}/game/${gameId}`),
          axios.get(`${API}/economy/rates`),
        ]);
        if (cancelled) return;
        setGame(g.data);
        setEconomyRates(r.data);
        setState(g.data?.checkers_state || null);
        setColorMap(g.data?.checkers_state?.color_map || {});
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load game');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [gameId]);

  // ---- WebSocket for live state updates + move dispatch ---------------
  useEffect(() => {
    if (!user?.id || !gameId) return;
    const wsUrl = (BACKEND_URL || '').replace('http', 'ws').replace('https', 'wss')
      + `/ws/game/${gameId}?user_id=${user.id}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'checkers_state') {
          setState(msg.state);
          if (msg.color_map) setColorMap(msg.color_map);
        }
      } catch { /* ignore */ }
    };
    ws.onerror = () => setError('Connection lost — try refreshing.');
    return () => ws.close();
  }, [user?.id, gameId]);

  // ---- Turn-timer countdown (server sets `turn_deadline` on state). ---
  useEffect(() => {
    if (!state?.turn_deadline) { setSecondsLeft(null); return; }
    const tick = () => {
      const remain = Math.max(0, Math.round(state.turn_deadline - Date.now() / 1000));
      setSecondsLeft(remain);
    };
    tick();
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
  }, [state?.turn_deadline]);

  // ---- Derivations ----------------------------------------------------
  const myColor = colorMap?.[user?.id];
  const wager = Number(game?.wager_amount || 0);
  const rake = economyRates?.rake_1v1 ?? 0.08;
  const prize = wager > 0 ? Math.floor(wager * 2 * (1 - rake)) : 0;
  const isPractice = !!game?.practice_mode;

  const dispatchMove = (move) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'checkers_move', move }));
  };
  const dispatchHuff = (pos) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: 'checkers_huff', pos }));
  };

  const players = game?.players || [];
  const opponent = players.find(p => p.id !== user?.id);

  if (loading) return <div className="p-8 text-center text-white">Loading checkers…</div>;
  if (error) return (
    <div className="p-8 text-center">
      <div className="text-red-400 mb-4">{error}</div>
      <button onClick={() => navigate('/')} className="text-emerald-400 underline">Back</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4" data-testid="checkers-multiplayer-page">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left panel — players + your color */}
        <aside className="lg:col-span-1 bg-black/40 border border-white/10 rounded-2xl p-4 space-y-3">
          <div>
            <div className="text-xs text-white/50 uppercase">You</div>
            <div className="text-lg font-bold" data-testid="checkers-my-name">{user?.name || 'You'}</div>
            <div className={`text-sm ${myColor === 'black' ? 'text-gray-300' : 'text-red-400'}`}>{myColor || 'spectator'}</div>
          </div>
          <div>
            <div className="text-xs text-white/50 uppercase">Opponent</div>
            <div className="text-lg font-bold" data-testid="checkers-opponent-name">{opponent?.name || '—'}</div>
          </div>
        </aside>

        {/* Board */}
        <div className="lg:col-span-2 flex flex-col items-center justify-center">
          {state ? (
            <CheckersBoard
              state={state}
              myColor={myColor}
              onMove={dispatchMove}
              onHuff={dispatchHuff}
              disabled={!!state?.winner}
            />
          ) : (
            <div className="text-white/60">Waiting for game to start…</div>
          )}
        </div>

        {/* Right panel — Entry, Prize, turn timer */}
        <aside className="lg:col-span-1 bg-black/40 border border-white/10 rounded-2xl p-4 space-y-3">
          {!isPractice && wager > 0 && (
            <>
              <div>
                <div className="text-xs text-white/50 uppercase">Entry</div>
                <div className="text-2xl font-bold text-emerald-400" data-testid="checkers-entry-amount">
                  {fmt(wager)} <span className="text-sm text-white/60">Emeralds</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-white/50 uppercase">Prize</div>
                <div className="text-2xl font-bold text-yellow-400" data-testid="checkers-prize-amount">
                  {fmt(prize)} <span className="text-sm text-white/60">Rubies</span>
                </div>
                <div className="text-[11px] text-white/40">
                  after {Math.round(rake * 100)}% GEMS fee
                </div>
              </div>
            </>
          )}
          {isPractice && (
            <div className="text-white/70 text-sm">Practice game · no entry, no prize</div>
          )}
          <div className="border-t border-white/10 pt-3">
            <div className="text-xs text-white/50 uppercase">Turn</div>
            <div className="text-lg font-bold capitalize" data-testid="checkers-turn-color">
              {state?.turn || '—'}
            </div>
            {secondsLeft != null && (
              <div className={`text-sm mt-1 ${secondsLeft <= 10 ? 'text-red-400' : 'text-white/70'}`}
                   data-testid="checkers-turn-timer">
                {secondsLeft}s left
              </div>
            )}
          </div>
          {state?.winner && (
            <div className="border-t border-white/10 pt-3">
              <div className="text-xs text-white/50 uppercase">Result</div>
              <div className="text-lg font-bold text-emerald-400" data-testid="checkers-result">
                {state.winner === 'draw' ? 'Draw' : `${state.winner} wins`}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default CheckersMultiplayerGame;
